// src/components/LoginModal.tsx

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { LoginForm } from './auth/LoginForm';
import { ForgotPasswordForm } from './auth/ForgotPasswordForm';
import { useNavigate } from 'react-router-dom';

const STORAGE_KEY = 'playclub_credentials';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (email: string, password: string, rememberMe: boolean) => Promise<void>;
  onRequestAccess?: () => void;
}

const LoginModal: React.FC<LoginModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  onRequestAccess
}) => {
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [email, setEmail] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const { email } = JSON.parse(stored);
        return email || '';
      }
    } catch {
      /* ignore */
    }
    return '';
  });
  const [password, setPassword] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const { password } = JSON.parse(stored);
        return password || '';
      }
    } catch {
      /* ignore */
    }
    return '';
  });
  const [rememberMe, setRememberMe] = useState(() => {
    try {
      return !!localStorage.getItem(STORAGE_KEY);
    } catch {
      return false;
    }
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // detect whether we're running inside the iOS WKWebView bridge
  const isIOSWebView = typeof window !== 'undefined' &&
    !!(window as any).webkit?.messageHandlers?.biometricAuth;

  // ─── Face ID callback wiring ────────────────────────────────────────────────
  useEffect(() => {
    // @ts-ignore
    window.onBiometricResult = async (success: boolean) => {
      if (!success) {
        setError('Face ID authentication failed');
        return;
      }
      // only attempt login if we have credentials
      if (email && password) {
        setIsLoading(true);
        setError('');
        try {
          await onSuccess(email, password, rememberMe);
        } catch (err) {
          console.error('Biometric login error', err);
          setError(err instanceof Error ? err.message : 'Login failed');
        } finally {
          setIsLoading(false);
        }
      }
    };
    return () => {
      // cleanup
      // @ts-ignore
      window.onBiometricResult = undefined;
    };
  }, [email, password, rememberMe, onSuccess]);
  // ─────────────────────────────────────────────────────────────────────────────

  const handleForgotPassword = () => {
    setShowForgotPassword(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await onSuccess(email, password, rememberMe);

      if (rememberMe) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ email, password }));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch (err) {
      let message = 'Failed to login';
      if (err instanceof Error) {
        if (err.message.includes('Invalid login credentials')) {
          message = 'Invalid email or password';
        } else if (err.message.includes('network')) {
          message = 'Network error. Please check your connection.';
        } else {
          message = err.message;
        }
      }
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestAccess = () => {
    onClose();
    onRequestAccess?.();
  };

  const handleRememberMeChange = (checked: boolean) => {
    setRememberMe(checked);
    if (!checked) {
      localStorage.removeItem(STORAGE_KEY);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[400px] bg-dark-200 text-white border-dark-300 p-6">
        <DialogHeader className="space-y-3">
          <DialogTitle className="text-2xl">Welcome Back</DialogTitle>
          <DialogDescription className="text-dark-800">
            Sign in to your playclub account to continue
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* only show Face ID on iOS WKWebView */}
          {isIOSWebView && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                onClick={() =>
                  // @ts-ignore
                  window.webkit.messageHandlers.biometricAuth.postMessage(null)
                }
                disabled={isLoading}
              >
                🔓 Login with Face ID
              </Button>
            </div>
          )}

          {showForgotPassword ? (
            <ForgotPasswordForm onBack={() => setShowForgotPassword(false)} />
          ) : (
            <LoginForm
              email={email}
              password={password}
              rememberMe={rememberMe}
              isLoading={isLoading}
              error={error}
              onEmailChange={setEmail}
              onPasswordChange={setPassword}
              onRememberMeChange={handleRememberMeChange}
              onSubmit={handleSubmit}
              onForgotPassword={handleForgotPassword}
            />
          )}

          <div className="text-center pt-2">
            <span className="text-dark-800 text-sm">
              Not a member yet?{' '}
              <button
                type="button"
                onClick={handleRequestAccess}
                className="text-gold-400 hover:text-gold-500 transition-colors font-medium"
              >
                Apply for Membership
              </button>
            </span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LoginModal;
